import _ from 'lodash';
import { captureIndexedDBError } from './utils';
import { hasLivingTab } from './activeTabs';

// Stale per-session report-state databases left behind by earlier sessions accumulate storage.
// These helpers enumerate and drop them, and live in the persistence layer (not pages) because
// they touch raw IndexedDB directly. The state databases are created elsewhere (dbState) at
// version 1; here we open each already-existing database at its reported version only to read its
// sessionInfo, recreating the store on the (unexpected) upgrade path as a safety net.
const STATE_OBJECT_STORE = 'STATE_RECOIL';
const STATE_DB_NAME_MARKER = 'DB_STATE_RECOIL';
const THREE_DAYS_MS = 259200000;

/**
 * Opens one persisted report-state database and reads back its session record so the caller can decide whether to drop it. Creates the STATE_RECOIL object store with a value index if the version triggers an upgrade, reads the sessionInfo entry in a readonly transaction, and flags indexedDBError in sessionStorage before rejecting when IndexedDB is missing.
 *
 * @param dbName - Name of the persisted report-state database to open.
 * @param versionDb - Schema version to open the database at, triggering an upgrade when it is newer.
 * @returns A promise resolving to an object carrying the stored sessionInfo data along with dbName and versionDb.
 */
const openWillDeleteDBState = (dbName: string, versionDb: number) =>
  new Promise((resolve, reject) => {
    let db: IDBDatabase;
    const indexedDB =
      window.indexedDB || window.webkitIndexedDB || window.mozIndexedDB || window.msIndexedDB;
    if (!indexedDB) {
      captureIndexedDBError(new Error('IndexedDB not supported'));
      reject(new Error('IndexedDB not supported'));
      return;
    }
    const request = indexedDB.open(dbName, versionDb);
    request.onupgradeneeded = function (event) {
      db = (event.target as IDBOpenDBRequest).result;
      // create object store
      const objectStore = db.createObjectStore(STATE_OBJECT_STORE, { keyPath: 'key' });
      // create indexes
      objectStore.createIndex('value', 'value', { unique: false });
    };

    request.onsuccess = function (event) {
      db = (event.target as IDBOpenDBRequest).result;
      const transaction = db.transaction([STATE_OBJECT_STORE], 'readonly');
      const objectStore = transaction.objectStore(STATE_OBJECT_STORE);
      const getSessionInfo = objectStore.get('sessionInfo');
      getSessionInfo.onsuccess = function (event) {
        const data = (event.target as IDBRequest).result;
        // Release the connection so the later deleteDatabase is not blocked by this handle.
        db.close();
        resolve({ data, dbName, versionDb });
      };
      getSessionInfo.onerror = function (event) {
        db.close();
        reject((event.target as IDBRequest).error);
      };
    };

    request.onerror = function (event) {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });

/**
 * Frees storage by dropping abandoned report-state databases left behind by earlier sessions. Enumerates every IndexedDB database whose name contains DB_STATE_RECOIL, reads each one's sessionInfo, and deletes any whose session time is older than three days — sparing a study still open in any browser tab, so a long-lived open session is never evicted out from under it.
 *
 * @returns A promise resolving once every stale report-state database has been deleted.
 */
export const deleteUnusedIndexDB = async () => {
  const idb =
    window.indexedDB || window.webkitIndexedDB || window.mozIndexedDB || window.msIndexedDB;
  if (!idb) return;

  const promises: Promise<any>[] = [];
  await idb.databases().then((r) => {
    r.forEach((data) => {
      if (data?.name?.includes(STATE_DB_NAME_MARKER)) {
        promises.push(openWillDeleteDBState(data?.name, data?.version));
      }
    });
  });

  const result = await Promise.all(promises);
  _.forEach(result, (info) => {
    const { data, dbName } = info || {};
    const { sessionTime } = data?.value || {};
    const currentTime = new Date().getTime();
    const sessionTimeInMs = new Date(sessionTime).getTime();
    const timeDiff = currentTime - sessionTimeInMs;
    // Clear the db when its session is older than 3 days, unless the study is still open in a tab
    // (a long-lived session keeps its sessionTime old but must not be evicted while in use).
    const studyId = dbName.split('_')[0];
    if (timeDiff > THREE_DAYS_MS && !hasLivingTab(studyId)) {
      idb.deleteDatabase(dbName);
    }
  });
};

// Per-study databases the app still uses, named `${studyId}_${suffix}`; anything else
// in IndexedDB (e.g. the retired DB_WORKER_ECG_INFO) is stale and gets dropped.
const KEEP_DB_SUFFIXES = ['DB_STATE_RECOIL', 'DB_STATE_HISTORY_RECOIL', 'DB_WORKER_BEAT_INFO'];
const KEEP_DB_NAMES = ['DB_METADATA'];

/**
 * Drops every IndexedDB database that is not one the report app still uses. Keeps the global DB_METADATA and any per-study state, undo-history and beat-worker database, and deletes everything else (such as the retired ECG database) so obsolete storage does not linger on the user's machine.
 *
 * @returns A promise resolving once the non-essential databases have been deleted.
 */
export const deleteNonEssentialDatabases = async () => {
  const idb =
    window.indexedDB || window.webkitIndexedDB || window.mozIndexedDB || window.msIndexedDB;
  if (!idb?.databases) return;

  const databases = await idb.databases();
  await Promise.all(
    databases
      .filter((db) => {
        const name = db?.name;
        if (!name || KEEP_DB_NAMES.includes(name)) return false;
        return !KEEP_DB_SUFFIXES.some((suffix) => name.endsWith(`_${suffix}`));
      })
      .map((db) => idb.deleteDatabase(db.name as string)),
  );
};

/**
 * Lists the IndexedDB databases this browser holds for the report app. Resolves the vendor-prefixed indexedDB handle and awaits its databases call, returning null when the browser exposes no IndexedDB at all.
 *
 * @returns A promise resolving to the database descriptor list, or null when IndexedDB is unavailable.
 */
export const getAllDatabases = async () => {
  const indexedDB =
    window.indexedDB || window.webkitIndexedDB || window.mozIndexedDB || window.msIndexedDB;
  if (indexedDB) {
    const databases = await indexedDB.databases();
    return databases;
  }
  return null;
};
